import React, { memo, useId } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '../../lib/formatDate';
import { RISK_LABEL, NDA_LABEL, CONTRACT_LABEL, REVIEW_STATUS_LABEL } from './vendorLabels';
import { vendorFlags } from './VendorBits';

/*
 * One vendor in the register: a row in a ruled list, not a box of its own.
 *
 * One DOM at every width. On desktop the four readings sit in columns under the
 * list's header row (their own dt labels are visually hidden there); below
 * 1024px the columns are hidden and a single muted summary line says the same
 * four readings in words. Only one of the two is ever displayed, so a screen
 * reader hears them once.
 *
 * Problems are the strongest signal: a red or amber edge on the row and small
 * flags beside the name. Archived is not a problem: a quiet tag and a greyed row.
 * The name is the row's only link; its ::after stretches over the row, so the
 * link's accessible name stays the vendor name, and nothing else in the row may
 * be interactive.
 */

const PROBLEM_TONE = {
  'Changed outside Nexus': 'warn',
  'Review overdue': 'bad',
  'NDA missing': 'bad',
};

const RISK_TONE = { low: 'ok', medium: 'warn', high: 'bad', critical: 'bad' };
const NDA_TONE = { signed: 'ok', pending: 'warn', expired: 'bad', not_required: 'neutral' };
const CONTRACT_TONE = { active: 'ok', draft: 'neutral', expired: 'bad', terminated: 'neutral', none: 'neutral' };
const REVIEW_TONE = { overdue: 'bad', due_soon: 'warn', ok: 'ok', never_reviewed_due: 'neutral' };

function Cell({ label, value, sub, tone }) {
  const empty = !value;
  return (
    <div className={`vl-cell vl-cell--${empty ? 'empty' : tone || 'neutral'}`}>
      <dt className="vl-cell__label">{label}</dt>
      <dd>
        <span className="vl-cell__value">{value || 'Not set'}</span>
        {sub && <span className="vl-cell__sub">{sub}</span>}
      </dd>
    </div>
  );
}

function contractOf(v) {
  const label = v.contract_status ? (CONTRACT_LABEL[v.contract_status] || v.contract_status) : null;
  const end = formatDate(v.contract_end);
  const ended = v.contract_status === 'expired' || v.contract_status === 'terminated';
  return {
    value: label || (end ? `Ends ${end}` : null),
    sub: label && end ? `${ended ? 'Ended' : 'Ends'} ${end}` : null,
    tone: CONTRACT_TONE[v.contract_status] || 'neutral',
  };
}

const lower = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

// "High risk · NDA signed · Contract active · Review 15/12/2026, overdue"
function summaryOf(v, contract, due, reviewLabel) {
  const risk = v.risk_level ? `${RISK_LABEL[v.risk_level] || v.risk_level} risk` : 'Risk not set';
  const nda = v.nda_status ? `NDA ${lower(NDA_LABEL[v.nda_status] || v.nda_status)}` : 'NDA not set';
  const con = v.contract_status ? `Contract ${lower(CONTRACT_LABEL[v.contract_status] || v.contract_status)}` : 'Contract not set';
  const rev = due ? `Review ${due}${reviewLabel ? `, ${lower(reviewLabel)}` : ''}` : 'Review not set';
  return [risk, nda, con, rev].join(' · ');
}

function VendorRow({ vendor: v }) {
  const headingId = useId();
  const problems = vendorFlags(v).filter((f) => PROBLEM_TONE[f]);
  const archived = v.active === false;
  const tone = problems.length ? (problems.some((f) => PROBLEM_TONE[f] === 'bad') ? 'bad' : 'warn') : null;
  const specs = v.specialities || [];
  const contract = contractOf(v);
  const due = formatDate(v.effective_next_review || v.next_review);
  const reviewLabel = REVIEW_STATUS_LABEL[v.review_status];
  const contact = [v.email, v.phone].filter(Boolean);
  const classes = ['vl-row', tone && `vl-row--${tone}`, archived && 'is-archived'].filter(Boolean).join(' ');

  return (
    <li className={classes}>
      <article className="vl-row__grid" aria-labelledby={headingId}>
        <div className="vl-row__vendor">
          <div className="vl-row__title">
            <h3 className="vl-row__name" id={headingId}>
              <Link className="vl-row__link" to={`/vendors/${v.id}`} dir="auto">{v.name}</Link>
            </h3>
            {problems.length > 0 && (
              <ul className="vl-flags" aria-label="Needs attention">
                {problems.map((f) => <li key={f} className={`vl-flag vl-flag--${PROBLEM_TONE[f]}`}>{f}</li>)}
              </ul>
            )}
            {archived && <span className="vl-tag">Archived</span>}
          </div>
          {specs.length > 0 ? (
            <ul className="vd-chips vl-row__specs" aria-label="Specialities">
              {specs.map((s) => <li key={s.id} className="vd-chip" dir="auto">{s.name}</li>)}
            </ul>
          ) : (
            <p className="vl-row__quiet">No speciality</p>
          )}
          <p className="vl-row__summary">{summaryOf(v, contract, due, reviewLabel)}</p>
          {contact.length > 0 && (
            <p className="vl-row__contact">
              {contact.map((c, i) => (
                <span key={c} dir="ltr">
                  {i > 0 && <span className="vl-row__sep" aria-hidden="true">·</span>}
                  {c}
                </span>
              ))}
            </p>
          )}
        </div>

        <dl className="vl-row__cells">
          <Cell label="Risk" value={v.risk_level ? (RISK_LABEL[v.risk_level] || v.risk_level) : null} tone={RISK_TONE[v.risk_level]} />
          <Cell
            label="NDA"
            value={v.nda_status ? (NDA_LABEL[v.nda_status] || v.nda_status) : null}
            // A high/critical vendor without a signed NDA is a gap whatever the status says.
            tone={problems.includes('NDA missing') ? 'bad' : NDA_TONE[v.nda_status]}
          />
          <Cell label="Contract" value={contract.value} sub={contract.sub} tone={contract.tone} />
          <Cell label="Next review" value={due} sub={reviewLabel} tone={REVIEW_TONE[v.review_status]} />
        </dl>
      </article>
    </li>
  );
}

// Memoised: the list re-renders on each keystroke in search while rows keep identity.
export default memo(VendorRow);
